import { getStringTag } from 'features/libra/helpers/getStringTag'
import { useSnapshotCreateFailed } from 'features/libra/stepComponents/ffs/FfsAssetModelSnapshotContext'
import { useFfsAssetModelSnapshot } from 'features/libra/stepComponents/ffs/useFfsAssetModelSnapshot'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentModel,
  GeckoInspection,
  GeckoUnit,
  RolodexEntryType,
} from 'types'

export interface FfsTicketContext {
  componentId: string | undefined
  inspectionId: string | undefined
  unitId: string | undefined
  component: GeckoComponent | undefined
  inspection: GeckoInspection | undefined
  unit: GeckoUnit | undefined
  /**
   * The asset model the ticket should read: for asset-scoped workflows this
   * is the ticket's SNAPSHOT entry (`ffs-asset-model_{ticketId}`), else the
   * unit's live `ASSET_MODEL` entry. Exposed so steps that want to mount
   * `EditGeckoRichAssetModel` inline (e.g. an "Edit Asset Model" drawer
   * triggered from a missing-input prompt) don't have to re-query -- and,
   * because the editor puts by the passed model's own `id`/`tags`, mounting
   * it with the snapshot model automatically targets the snapshot entry.
   */
  assetModel: GeckoAssetModel | undefined
  /** True when `assetModel` is the ticket-scoped snapshot. */
  assetModelIsSnapshot: boolean
  /**
   * True when the lazy snapshot create failed (see
   * `useFfsAssetModelSnapshot`); the context is serving the live model.
   */
  snapshotCreateFailed: boolean
  /**
   * Typed asset-model component model (e.g. `GeckoPipeSection`,
   * `GeckoPressureVesselShell`). Source of truth for rich geometry like
   * `radius` and `wallNominalThickness` -- neither of which lives on the
   * Rolodex `component` entry above. Undefined until the asset model loads
   * AND the ticket's component has been added to it.
   */
  componentModel: GeckoComponentModel | undefined
  isLoading: boolean
  /**
   * Refreshes every entry the context owns. Pass to `EditGeckoRichAssetModel`'s
   * `refreshData` so post-edit writes invalidate downstream consumers.
   */
  forceRefresh: () => Promise<void>
}

/**
 * Reads `(component, inspection, unit)` from the ticket's tags and fetches
 * the relevant Rolodex entries.
 *
 * Uses `useRolodexEntriesWithSyncedRefreshes` so the queries participate in
 * the workspace's synced refresh registry. **Must be called inside a
 * `RolodexEntriesWithSyncedRefreshesProvider`** -- `LibraTicketWorkspaceContainer`
 * mounts that provider for FFS tickets.
 */
export function useFfsTicketContext(): FfsTicketContext {
  const { ticketTags } = useTicketAccess()
  // Prefer the surrounding `ComponentScopeContext` (asset FFS tickets — the
  // tabs above the workspace layout mount it). Fall back to ticket tags
  // so the legacy single-component FFS workflow keeps working until those
  // tickets are migrated.
  const scope = useComponentScope()

  const componentId =
    scope?.componentId ?? getStringTag(ticketTags, RolodexEntryType.COMPONENT)
  const inspectionId =
    scope?.inspectionId ?? getStringTag(ticketTags, RolodexEntryType.INSPECTION)
  const ticketUnitId = getStringTag(ticketTags, RolodexEntryType.UNIT)

  const {
    entries: componentEntries,
    isLoading: componentLoading,
    forceRefresh: refreshComponent,
  } = useRolodexEntriesWithSyncedRefreshes(
    componentId
      ? { type: RolodexEntryType.COMPONENT, ids: [componentId] }
      : undefined
  )

  // FFS tickets created via certain paths bind COMPONENT only -- the
  // component entry's `unit` tag is the source of truth. Fall back to it
  // when the ticket itself lacks a UNIT tag so unit/asset-model queries
  // and validations work consistently across every FFS hook + step.
  const firstComponent = componentEntries[0] as GeckoComponent | undefined
  const derivedUnitFromComponent = firstComponent
    ? getStringTag(firstComponent.tags, RolodexEntryType.UNIT)
    : undefined
  const unitId = ticketUnitId ?? derivedUnitFromComponent
  const {
    entries: inspectionEntries,
    isLoading: inspectionLoading,
    forceRefresh: refreshInspection,
  } = useRolodexEntriesWithSyncedRefreshes(
    inspectionId
      ? { type: RolodexEntryType.INSPECTION, ids: [inspectionId] }
      : undefined
  )
  const {
    entries: unitEntries,
    isLoading: unitLoading,
    forceRefresh: refreshUnit,
  } = useRolodexEntriesWithSyncedRefreshes(
    unitId ? { type: RolodexEntryType.UNIT, ids: [unitId] } : undefined
  )

  // Asset model resolution (snapshot-first for asset-scoped workflows,
  // live otherwise) lives in the shared hook so this context and
  // `useFfsComponentCdfs` can't drift; snapshot CREATION is owned solely
  // by `FfsAssetModelSnapshotProvider`. Consumers read the typed
  // `componentModel` (the source of truth for rich geometry like
  // `radius` and `wallNominalThickness`) without re-querying.
  const {
    assetModel,
    isSnapshot: assetModelIsSnapshot,
    isLoading: assetModelLoading,
    forceRefresh: refreshAssetModel,
  } = useFfsAssetModelSnapshot()
  const snapshotCreateFailed = useSnapshotCreateFailed()

  const forceRefresh = useCallback(async (): Promise<void> => {
    await Promise.all([
      refreshComponent(),
      refreshInspection(),
      refreshUnit(),
      refreshAssetModel(),
    ])
  }, [refreshComponent, refreshInspection, refreshUnit, refreshAssetModel])

  return useMemo(() => {
    const componentModel =
      componentId && assetModel
        ? assetModel.data.components.get(componentId)
        : undefined

    return {
      componentId,
      inspectionId,
      unitId,
      component: componentEntries[0] as GeckoComponent | undefined,
      inspection: inspectionEntries[0] as GeckoInspection | undefined,
      unit: unitEntries[0] as GeckoUnit | undefined,
      assetModel,
      assetModelIsSnapshot,
      snapshotCreateFailed,
      componentModel,
      isLoading:
        componentLoading ||
        inspectionLoading ||
        unitLoading ||
        assetModelLoading,
      forceRefresh,
    }
  }, [
    componentId,
    inspectionId,
    unitId,
    componentEntries,
    inspectionEntries,
    unitEntries,
    assetModel,
    assetModelIsSnapshot,
    snapshotCreateFailed,
    componentLoading,
    inspectionLoading,
    unitLoading,
    assetModelLoading,
    forceRefresh,
  ])
}
